import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// All available section permissions
const SECTION_PERMISSIONS = [
  { key: 'schedule', label: 'Schedule', description: 'View and manage project schedule/tasks' },
  { key: 'resources', label: 'Resources', description: 'View and manage project team/contacts' },
  { key: 'submittals', label: 'Submittals', description: 'View and manage submittals' },
  { key: 'rfis', label: 'RFIs', description: 'View and manage RFIs' },
  { key: 'budget', label: 'Budget', description: 'View and manage project budget' },
  { key: 'change_orders', label: 'Change Orders', description: 'View and manage change orders' },
  { key: 'meeting_minutes', label: 'Meeting Minutes', description: 'View and manage meeting minutes' }
];

const getDefaultPermissions = () => {
  const perms = {};
  SECTION_PERMISSIONS.forEach(p => { perms[p.key] = false; });
  return perms;
};

export const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [inviteLink, setInviteLink] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showProjectAccessModal, setShowProjectAccessModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userProjects, setUserProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [permissions, setPermissions] = useState(getDefaultPermissions());
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteEmailSent, setInviteEmailSent] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchProjects();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects');
    }
  };

  const createInvite = async (sendEmail = false) => {
    setSendingInvite(true);
    try {
      const params = new URLSearchParams();
      if (inviteEmail) {
        params.append('email', inviteEmail);
      }
      if (sendEmail && inviteEmail) {
        params.append('send_email_invite', 'true');
      }
      
      const response = await axios.post(`${API}/users/create-invite?${params.toString()}`);
      setInviteLink(response.data.invite_link);
      setInviteEmailSent(response.data.email_sent || false);
      setShowInviteModal(true);
      
      if (response.data.email_sent) {
        toast.success(`Invite email sent to ${inviteEmail}`);
      }
    } catch (error) {
      toast.error('Failed to create invite');
    } finally {
      setSendingInvite(false);
    }
  };

  const resetInviteModal = () => {
    setShowInviteModal(false);
    setInviteLink('');
    setInviteEmail('');
    setInviteEmailSent(false);
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success('User deleted');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const toggleUserRole = async (user) => {
    const newRole = user.role === 'admin' ? 'user' : 'admin';
    const action = newRole === 'admin' ? 'promote to Admin' : 'demote to User';
    
    if (!window.confirm(`Are you sure you want to ${action} "${user.full_name}"?`)) return;
    
    try {
      await axios.patch(`${API}/users/${user.id}/role`, null, {
        params: { role: newRole }
      });
      toast.success(`User ${newRole === 'admin' ? 'promoted to Admin' : 'demoted to User'}`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user role');
    }
  };

  const openProjectAccessModal = async (user) => {
    setSelectedUser(user);
    setSelectedProject('');
    setPermissions(getDefaultPermissions());
    
    // Fetch user's current project access
    try {
      const projectAccessPromises = projects.map(async (project) => {
        const response = await axios.get(`${API}/projects/${project.id}/members`);
        const userMember = response.data.find(m => m.user_id === user.id);
        if (userMember) {
          return {
            project_id: project.id,
            project_name: project.name,
            permissions: userMember.permissions || []
          };
        }
        return null;
      });
      
      const results = await Promise.all(projectAccessPromises);
      setUserProjects(results.filter(r => r !== null));
    } catch (error) {
      console.error('Failed to fetch user projects');
    }
    
    setShowProjectAccessModal(true);
  };

  const addProjectAccess = async () => {
    if (!selectedProject) {
      toast.error('Please select a project');
      return;
    }

    const selectedPermissions = Object.keys(permissions).filter(key => permissions[key]);
    
    if (selectedPermissions.length === 0) {
      toast.error('Please select at least one permission');
      return;
    }

    try {
      await axios.post(`${API}/projects/${selectedProject}/members`, {
        user_id: selectedUser.id,
        permissions: selectedPermissions
      });
      
      toast.success('Project access granted');
      
      // Refresh user projects
      const project = projects.find(p => p.id === selectedProject);
      setUserProjects([...userProjects, {
        project_id: selectedProject,
        project_name: project.name,
        permissions: selectedPermissions
      }]);
      
      // Reset form
      setSelectedProject('');
      setPermissions(getDefaultPermissions());
    } catch (error) {
      toast.error('Failed to grant project access');
    }
  };

  const removeProjectAccess = async (projectId) => {
    if (!window.confirm('Remove user from this project?')) return;
    
    try {
      await axios.delete(`${API}/projects/${projectId}/members/${selectedUser.id}`);
      toast.success('Project access removed');
      setUserProjects(userProjects.filter(p => p.project_id !== projectId));
    } catch (error) {
      toast.error('Failed to remove project access');
    }
  };

  const togglePermission = (permission) => {
    setPermissions({ ...permissions, [permission]: !permissions[permission] });
  };

  const toggleAllPermissions = () => {
    const allSelected = Object.values(permissions).every(v => v);
    const newPermissions = {};
    Object.keys(permissions).forEach(key => {
      newPermissions[key] = !allSelected;
    });
    setPermissions(newPermissions);
  };

  return (
    <div className="space-y-6" data-testid="users-management-container">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-slate-900">Users</h1>
          <p className="mt-2 text-base text-slate-600">Manage user accounts and project access</p>
        </div>
        <button 
          onClick={() => setShowInviteModal(true)} 
          className="bg-slate-900 text-white px-4 py-2 rounded-md hover:bg-slate-800" 
          data-testid="create-invite-button"
        >
          Invite User
        </button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full" data-testid="users-table">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Name</th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Email</th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Role</th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">2FA</th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} className="border-b hover:bg-slate-50" data-testid={`user-row-${user.id}`}>
                <td className="p-4 font-medium">{user.full_name}</td>
                <td className="p-4">{user.email}</td>
                <td className="p-4">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    user.role === 'admin' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'
                  }`}>
                    {user.role}
                  </span>
                </td>
                <td className="p-4">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    user.is_2fa_enabled ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'
                  }`}>
                    {user.is_2fa_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </td>
                <td className="p-4">
                  <div className="flex gap-3">
                    <button 
                      onClick={() => toggleUserRole(user)} 
                      className={`text-sm hover:underline ${
                        user.role === 'admin' ? 'text-amber-600' : 'text-green-600'
                      }`}
                      data-testid={`toggle-role-${user.id}`}
                    >
                      {user.role === 'admin' ? 'Remove Admin' : 'Make Admin'}
                    </button>
                    <button 
                      onClick={() => openProjectAccessModal(user)} 
                      className="text-sm text-blue-600 hover:underline"
                      data-testid={`manage-access-${user.id}`}
                    >
                      Manage Projects
                    </button>
                    <button 
                      onClick={() => deleteUser(user.id)} 
                      className="text-sm text-red-600 hover:underline" 
                      data-testid={`delete-user-${user.id}`}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={resetInviteModal}>
          <div className="bg-white rounded-lg p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="invite-modal">
            {!inviteLink ? (
              // Step 1: Enter email and choose method
              <>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">Invite New User</h2>
                <p className="text-sm text-slate-600 mb-4">Enter the user's email address (optional)</p>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email Address</label>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="user@example.com"
                    className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
                    data-testid="invite-email-input"
                  />
                </div>
                
                <div className="space-y-3">
                  <button 
                    onClick={() => createInvite(true)}
                    disabled={!inviteEmail || sendingInvite}
                    className="w-full bg-indigo-600 text-white py-2 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    data-testid="send-email-invite-button"
                  >
                    {sendingInvite ? (
                      <>
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Send Invite via Email
                      </>
                    )}
                  </button>
                  
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-slate-200"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-slate-500">or</span>
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => createInvite(false)}
                    disabled={sendingInvite}
                    className="w-full border border-slate-200 text-slate-700 py-2 rounded-md hover:bg-slate-50 disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="get-link-button"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                    Get Link to Share
                  </button>
                </div>
                
                <button 
                  onClick={resetInviteModal} 
                  className="w-full mt-4 text-sm text-slate-500 hover:text-slate-700"
                >
                  Cancel
                </button>
              </>
            ) : (
              // Step 2: Show the invite link
              <>
                <div className="text-center mb-4">
                  {inviteEmailSent ? (
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  ) : (
                    <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                    </div>
                  )}
                  <h2 className="text-2xl font-bold text-slate-900">
                    {inviteEmailSent ? 'Invite Sent!' : 'Invite Link Created'}
                  </h2>
                </div>
                
                {inviteEmailSent && (
                  <p className="text-sm text-slate-600 mb-4 text-center">
                    An email has been sent to <strong>{inviteEmail}</strong> with the invite link.
                  </p>
                )}
                
                <p className="text-sm text-slate-600 mb-2">You can also share this link directly:</p>
                <div className="bg-slate-50 p-3 rounded border border-slate-200 mb-4 break-all text-sm" data-testid="invite-link">
                  {inviteLink}
                </div>
                <button 
                  onClick={() => { navigator.clipboard.writeText(inviteLink); toast.success('Link copied!'); }} 
                  className="w-full bg-slate-900 text-white py-2 rounded-md hover:bg-slate-800 mb-3" 
                  data-testid="copy-invite-button"
                >
                  Copy Link
                </button>
                <button 
                  onClick={resetInviteModal} 
                  className="w-full border border-slate-200 text-slate-700 py-2 rounded-md hover:bg-slate-50"
                >
                  Done
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Project Access Modal */}
      {showProjectAccessModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowProjectAccessModal(false)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="project-access-modal">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Manage Project Access</h2>
            <p className="text-slate-600 mb-6">User: <strong>{selectedUser.full_name}</strong> ({selectedUser.email})</p>

            {/* Current Project Access */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-3">Current Project Access</h3>
              {userProjects.length === 0 ? (
                <p className="text-slate-500 text-sm">No project access granted yet</p>
              ) : (
                <div className="space-y-2">
                  {userProjects.map((up) => (
                    <div key={up.project_id} className="flex items-center justify-between p-4 border border-slate-200 rounded-lg bg-slate-50">
                      <div>
                        <div className="font-medium text-slate-900">{up.project_name}</div>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {up.permissions.length > 0 ? (
                            up.permissions.map(perm => {
                              const permInfo = SECTION_PERMISSIONS.find(p => p.key === perm);
                              return (
                                <span 
                                  key={perm} 
                                  className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs font-medium"
                                >
                                  {permInfo?.label || perm.replace('_', ' ')}
                                </span>
                              );
                            })
                          ) : (
                            <span className="text-sm text-slate-500">No permissions</span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => removeProjectAccess(up.project_id)}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add New Project Access */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Add Project Access</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Select Project</label>
                  <select
                    value={selectedProject}
                    onChange={(e) => setSelectedProject(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md"
                  >
                    <option value="">Choose a project...</option>
                    {projects
                      .filter(p => !userProjects.find(up => up.project_id === p.id))
                      .map(project => (
                        <option key={project.id} value={project.id}>{project.name}</option>
                      ))
                    }
                  </select>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-slate-700">Section Permissions</label>
                    <button
                      type="button"
                      onClick={toggleAllPermissions}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      {Object.values(permissions).every(v => v) ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {SECTION_PERMISSIONS.map((perm) => (
                      <label 
                        key={perm.key} 
                        className={`flex items-start space-x-3 p-3 border rounded-md cursor-pointer transition-colors ${
                          permissions[perm.key] 
                            ? 'border-indigo-500 bg-indigo-50' 
                            : 'border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={permissions[perm.key]}
                          onChange={() => togglePermission(perm.key)}
                          className="w-4 h-4 mt-0.5 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                        />
                        <div>
                          <span className="text-sm font-medium text-slate-900">
                            {perm.label}
                          </span>
                          <p className="text-xs text-slate-500 mt-0.5">{perm.description}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 mt-3">
                    Dashboard is always accessible. Select additional sections this user can access.
                  </p>
                </div>

                <button
                  onClick={addProjectAccess}
                  className="w-full bg-slate-900 text-white py-2 rounded-md hover:bg-slate-800"
                >
                  Grant Access
                </button>
              </div>
            </div>

            <button 
              onClick={() => setShowProjectAccessModal(false)} 
              className="w-full mt-4 border border-slate-200 py-2 rounded-md hover:bg-slate-50"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
